import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import { ButtonGroup } from "@mui/material";
import { easeOut } from "ol/easing.js";
import { fromLonLat } from "ol/proj.js";
import { Button } from "components/ui/button";
import OpenLayersMap, {
  MAP_TRANSITION_DURATION,
} from "components/maps/OpenLayersMap";
import { PNOA_MAX_ZOOM } from "components/maps/layers/pnoa";
import { getValidSensorPointCoordinate } from "components/maps/sensorGeometry";
import { hasPermission } from "components/authentication/hasPermission";
import { useStateContext } from "contexts/ContextProvider";
import { ReactComponent as LoadingMap } from "icons/loading-map.svg";
import { Progress } from "components/ui/progress";
import Scene from "./Scene";

const WEB_MERCATOR = "EPSG:3857";
export const DEFAULT_SENSOR_FOCUS_ZOOM = 17;
export const ANALYTICS_SENSOR_FOCUS_ZOOM = 18;

const selectionScopeFor = (activeLinkName) =>
  ["analytics", "anExpanded"].includes(activeLinkName)
    ? "analytics"
    : activeLinkName;

const MapGis = memo(({ data, selectedRowIds, onRowSelectionChange }) => {
  const mapRef = useRef(null);
  const {
    activeLinkName,
    isLoadingUserPermissions,
    setIsLoadingUserPermissions,
  } = useStateContext();

  const [allowInitialRouteFit, setAllowInitialRouteFit] = useState(true);
  const [focusedSensorId, setFocusedSensorId] = useState(null);
  const previousSelectedSensorIdsRef = useRef([]);
  const previousSelectionScopeRef = useRef(
    selectionScopeFor(activeLinkName)
  );
  const selectionScope = selectionScopeFor(activeLinkName);
  const selectionScopeChanged =
    previousSelectionScopeRef.current !== selectionScope;
  const effectiveSelectedRowIds = useMemo(
    () => (selectionScopeChanged ? [] : selectedRowIds),
    [selectedRowIds, selectionScopeChanged]
  );
  const isAnalyticsView = selectionScope === "analytics";
  const sensorFocusZoom =
    isAnalyticsView
      ? ANALYTICS_SENSOR_FOCUS_ZOOM
      : DEFAULT_SENSOR_FOCUS_ZOOM;
  const hasValidSelectedSensor = effectiveSelectedRowIds.some((rowId) =>
    getValidSensorPointCoordinate(
      data.find((entry) => entry.id === rowId)
    )
  );
  const canEditMap = hasPermission(
    "map.edit",
    isLoadingUserPermissions,
    setIsLoadingUserPermissions
  );
  const canViewFullMap = hasPermission(
    "fullMap.view",
    isLoadingUserPermissions,
    setIsLoadingUserPermissions
  );

  const animateMapView = useCallback((nextCenter, nextZoom) => {
    const view = mapRef.current?.getView?.();
    if (!view) return;

    const animation = {
      duration: MAP_TRANSITION_DURATION,
      easing: easeOut,
    };
    if (nextCenter) {
      animation.center = fromLonLat(nextCenter, WEB_MERCATOR);
    }
    if (Number.isFinite(nextZoom)) animation.zoom = nextZoom;
    view.cancelAnimations?.();
    view.animate(animation);
  }, []);

  const handleMapReady = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isDelayOver, setIsDelayOver] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingDuration = 0;
  const intervalDuration = 300;

  const generateRandomIncrements = (total, length) => {
    const arr = Array.from({ length }, () => Math.random());
    const sum = arr.reduce((acc, value) => acc + value, 0);
    return arr.map((value) => (value / sum) * total);
  };

  useEffect(() => {
    let increments = [];
    if (isLoadingMap || !isDelayOver) {
      increments = generateRandomIncrements(
        100,
        loadingDuration / intervalDuration
      );
    }
    let timer;
    let index = 0;
    if (increments.length > 0) {
      timer = setInterval(() => {
        setLoadingProgress((prevProgress) => {
          if (index >= increments.length) {
            clearInterval(timer);
            return 100;
          }
          const newProgress = prevProgress + increments[index];
          index++;
          return Math.min(newProgress, 100);
        });
      }, intervalDuration);
    }
    return () => clearInterval(timer);
  }, [isLoadingMap, isDelayOver, loadingDuration, intervalDuration]);

  const selectedSensors = useMemo(
    () =>
      effectiveSelectedRowIds
        .map((rowId) => data.find((entry) => entry.id === rowId))
        .filter((sensor) => sensor !== undefined),
    [data, effectiveSelectedRowIds]
  );

  useEffect(() => {
    const validSelectedIds = selectedSensors
      .filter((sensor) => getValidSensorPointCoordinate(sensor))
      .map((sensor) => sensor.id);
    const newlySelectedIds = validSelectedIds.filter(
      (sensorId) => !previousSelectedSensorIdsRef.current.includes(sensorId)
    );

    setFocusedSensorId((currentFocusedId) => {
      if (newlySelectedIds.length > 0) {
        return newlySelectedIds[newlySelectedIds.length - 1];
      }
      if (currentFocusedId && validSelectedIds.includes(currentFocusedId)) {
        return currentFocusedId;
      }
      return validSelectedIds[validSelectedIds.length - 1] || null;
    });

    if (validSelectedIds.length > 0) setAllowInitialRouteFit(false);
    previousSelectedSensorIdsRef.current = [...effectiveSelectedRowIds];
  }, [effectiveSelectedRowIds, selectedSensors]);

  useEffect(() => {
    if (previousSelectionScopeRef.current !== selectionScope) {
      previousSelectionScopeRef.current = selectionScope;
      previousSelectedSensorIdsRef.current = [];
      setFocusedSensorId(null);
      setAllowInitialRouteFit(true);
      onRowSelectionChange?.([]);
    }
  }, [onRowSelectionChange, selectionScope]);

  const handleVectorSensorSelect = useCallback(
    (sensor) => {
      const coordinate = getValidSensorPointCoordinate(sensor);
      if (!coordinate || !sensor.apiSensor?.id) return;

      setAllowInitialRouteFit(false);
      setFocusedSensorId(sensor.apiSensor.id);
      const nextSelectedSensorIds = isAnalyticsView
        ? effectiveSelectedRowIds.includes(sensor.apiSensor.id)
          ? effectiveSelectedRowIds
          : [...effectiveSelectedRowIds, sensor.apiSensor.id]
        : [sensor.apiSensor.id];
      onRowSelectionChange?.(nextSelectedSensorIds);
    },
    [effectiveSelectedRowIds, isAnalyticsView, onRowSelectionChange]
  );

  const handleVectorSensorDeselect = useCallback(
    (sensorId) => {
      const remainingSensorIds = effectiveSelectedRowIds.filter(
        (selectedSensorId) => selectedSensorId !== sensorId
      );
      setFocusedSensorId((currentFocusedId) =>
        currentFocusedId === sensorId
          ? remainingSensorIds[remainingSensorIds.length - 1] || null
          : currentFocusedId
      );
      if (remainingSensorIds.length === 0) setAllowInitialRouteFit(true);
      onRowSelectionChange?.(remainingSensorIds);
    },
    [effectiveSelectedRowIds, onRowSelectionChange]
  );

  const handleClearSensorSelection = useCallback(() => {
    previousSelectedSensorIdsRef.current = [];
    setFocusedSensorId(null);
    setAllowInitialRouteFit(true);
    onRowSelectionChange?.([]);
  }, [onRowSelectionChange]);

  useEffect(() => {
    const timer = setTimeout(() => setIsDelayOver(true), loadingDuration);
    return () => clearTimeout(timer);
  }, [loadingDuration]);

  const finishMapLoading = () => {
    setIsLoadingMap(true);
    setTimeout(() => setIsLoadingMap(false), 800);
  };

  const handleZoomIn = (event) => {
    event.preventDefault();
    setAllowInitialRouteFit(false);
    const currentZoom = mapRef.current?.getView?.().getZoom?.();
    if (Number.isFinite(currentZoom)) {
      animateMapView(null, Math.min(currentZoom + 1, PNOA_MAX_ZOOM));
    }
    finishMapLoading();
  };

  const handleZoomOut = (event) => {
    event.preventDefault();
    setAllowInitialRouteFit(false);
    const currentZoom = mapRef.current?.getView?.().getZoom?.();
    if (Number.isFinite(currentZoom)) {
      animateMapView(null, Math.max(currentZoom - 1, 0));
    }
    finishMapLoading();
  };

  const handleGetLocation = (event) => {
    event.preventDefault();
    setAllowInitialRouteFit(false);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFocusedSensorId(null);
          animateMapView([longitude, latitude]);
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const [isFullMap, setIsFullMap] = useState(false);

  useEffect(() => {
    if (activeLinkName === "maps" && canEditMap) {
      setIsFullMap(true);
    } else {
      setIsFullMap(false);
    }
  }, [activeLinkName, canEditMap, canViewFullMap]);

  const [show3D, setShow3D] = useState(false);

  useEffect(() => {
    if (show3D) mapRef.current = null;
  }, [show3D]);

  return (
    <div
      id="iFrame-parent"
      className="p-0 overflow-clip min-h-20 w-full relative h-full"
    >
      {(isLoadingMap || !isDelayOver) && (
        <div className="absolute flex items-center justify-center inset-0 pointer-events-none z-20 bg-white dark:bg-gray-950">
          <Progress
            value={loadingProgress}
            className="z-20 absolute w-10/12 mx-auto h-3 left-1/2 top-1/2 max-w-md -translate-x-1/2 -translate-y-1/2"
          />
          <div className="w-full h-full opacity-50 invert dark:invert-0">
            <LoadingMap className="w-full animate-pulse-slow object-cover rounded-md h-full text-gray-950" />
          </div>
        </div>
      )}
      {!isFullMap && (
        <>
          {!show3D && (
            <ButtonGroup className="flex flex-col absolute top-2 left-2 z-[11] shadow-sm">
              <Button
                className="size-8 p-1 bg-white text-gray-600 dark:text-gray-200 hover:text-white dark:hover:text-white dark:bg-gray-900 rounded-b-none"
                size="icon"
                variant="default"
                onClick={handleZoomIn}
              >
                <Icon icon="mdi:plus" className="size-6" />
              </Button>
              <Button
                className="size-8 p-1 bg-white text-gray-600 dark:text-gray-200 hover:text-white dark:hover:text-white dark:bg-gray-900 rounded-t-none border-t-gray-500 border-t dark:border-t-gray-1000"
                size="icon"
                variant="default"
                onClick={handleZoomOut}
              >
                <Icon icon="mdi:minus" className="size-6" />
              </Button>
            </ButtonGroup>
          )}
          <ButtonGroup className="flex flex-col gap-2 absolute bottom-2 z-[11] right-2 shadow-sm">
            <Button
              aria-label="Centrar en mi ubicacion"
              className="size-8 p-1 bg-white text-gray-600 dark:text-gray-200 hover:text-white dark:hover:text-white dark:bg-gray-900"
              size="icon"
              variant="default"
              onClick={handleGetLocation}
            >
              <Icon icon="mdi:crosshairs-gps" className="size-6" />
            </Button>
            <Button
              aria-label={show3D ? "Mostrar mapa 2D" : "Mostrar vista 3D"}
              className="size-8 p-1 bg-white text-gray-600 dark:text-gray-200 hover:text-white dark:hover:text-white dark:bg-gray-900"
              size="icon"
              variant="default"
              onClick={() => setShow3D(!show3D)}
            >
              <Icon icon="mdi:cube-outline" className="size-6" />
            </Button>
          </ButtonGroup>
        </>
      )}
      <div className="absolute inset-0 pointer-events-none z-10 dark:backdrop-brightness-110 backdrop-saturate-125 dark:backdrop-hue-rotate-[170deg] dark:backdrop-invert-[90%] dark:backdrop-grayscale-[30%]" />
      <div className="h-full w-full flex overflow-clip rounded-sm">
        <div className="w-full h-full rounded overflow-clip">
          {show3D ? (
            <Scene data={selectedSensors} />
          ) : (
            <>
              <OpenLayersMap
                allowInitialRouteFit={
                  allowInitialRouteFit && !hasValidSelectedSensor
                }
                sensorData={data}
                selectedSensorIds={effectiveSelectedRowIds}
                focusedSensorId={focusedSensorId}
                sensorFocusZoom={sensorFocusZoom}
                onSensorSelect={handleVectorSensorSelect}
                onSensorDeselect={handleVectorSensorDeselect}
                onClearSensorSelection={handleClearSensorSelection}
                onMapReady={handleMapReady}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default MapGis;
